import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { AlertTriangle, ArrowDownLeft, Flag, Sliders, CheckCheck } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const fmt = (v) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(v || 0);

// Worst non-empty aging bucket -> label + dot colour.
function agingInfo(aging) {
  if (!aging) return null;
  if (aging.d90_plus > 0) return { label: '90+ days', dot: '#dc2626', cls: 'bg-red-100 text-red-700' };
  if (aging.d61_90 > 0) return { label: '61–90 days', dot: '#ea580c', cls: 'bg-orange-100 text-orange-700' };
  if (aging.d31_60 > 0) return { label: '31–60 days', dot: '#d97706', cls: 'bg-amber-100 text-amber-700' };
  if (aging.d0_30 > 0) return { label: '0–30 days', dot: '#ca8a04', cls: 'bg-yellow-100 text-yellow-700' };
  return null;
}

const ENTRY_META = {
  opening: { label: 'Opening', Icon: Flag, cls: 'text-stone-600' },
  accrual: { label: 'Accrued', Icon: AlertTriangle, cls: 'text-red-600' },
  payment: { label: 'Payment', Icon: ArrowDownLeft, cls: 'text-green-600' },
  adjustment: { label: 'Adjustment', Icon: Sliders, cls: 'text-amber-600' },
};

// Inline, ledger-backed arrears control for the forecast table's Arrears column.
export default function ArrearsCell({ expense, info, onRecord }) {
  const balance = info?.balance || 0;
  const entries = info?.entries || [];
  const aging = agingInfo(info?.aging);
  const [open, setOpen] = useState(false);
  const [payVal, setPayVal] = useState('');
  const [adjType, setAdjType] = useState('opening');
  const [adjVal, setAdjVal] = useState('');
  const [busy, setBusy] = useState(false);

  const record = async (entry, done) => {
    if (busy) return;
    try {
      setBusy(true);
      await onRecord(expense.id, entry);
      toast.success('Balance updated');
      if (done) done();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record entry');
    } finally {
      setBusy(false);
    }
  };

  const recordPayment = () => {
    const v = parseFloat(payVal);
    if (!v || v <= 0) { toast.error('Enter a payment amount'); return; }
    record({ entry_type: 'payment', amount: v, note: 'Payment' }, () => setPayVal(''));
  };
  const markPaid = () => record(
    { entry_type: 'payment', amount: balance, note: 'Marked paid in full' },
    () => setOpen(false)
  );
  const addAdjust = () => {
    let v = parseFloat(adjVal);
    if (!v || v === 0) { toast.error('Enter an amount'); return; }
    if (adjType === 'opening') v = Math.abs(v);
    record({ entry_type: adjType, amount: v, note: adjType === 'opening' ? 'Opening balance' : 'Adjustment' },
      () => setAdjVal(''));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`font-mono text-sm inline-flex items-center gap-1.5 rounded-[4px] px-1.5 py-0.5 -mx-1.5 hover:bg-stone-100 ${balance > 0 ? 'font-bold text-red-600' : 'text-stone-400 hover:text-red-500'}`}
          title={balance > 0 ? 'Click to record a payment or adjust' : 'Click to add an opening balance'}
          onClick={(e) => e.stopPropagation()}
          data-testid={`arrears-${expense.id}`}
        >
          {balance > 0 ? fmt(balance) : '+ add'}
          {aging && <span style={{ width: 7, height: 7, borderRadius: '50%', background: aging.dot }} />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-stone-900 truncate">{expense.name}</span>
          <span className={`font-mono text-sm font-bold ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(balance)}</span>
        </div>
        {aging && (
          <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full mb-2 ${aging.cls}`}>{aging.label} overdue</span>
        )}

        {/* Ledger history */}
        <div className="max-h-40 overflow-y-auto border-t border-stone-100 pt-2 mb-3">
          {entries.length === 0 ? (
            <p className="text-xs text-stone-500 py-1">No balance history yet.</p>
          ) : (
            entries.map((en) => {
              const meta = ENTRY_META[en.entry_type] || ENTRY_META.adjustment;
              const Icon = meta.Icon;
              return (
                <div key={en.id} className="flex items-center gap-2 text-xs py-1">
                  <Icon className={`w-3.5 h-3.5 ${meta.cls}`} />
                  <span className="flex-1 min-w-0 truncate text-stone-600">
                    {en.note || meta.label}{en.period ? ` · ${en.period}` : ''}
                  </span>
                  <span className="text-[10px] text-stone-400">
                    {en.created_at ? format(parseISO(en.created_at), 'dd MMM') : ''}
                  </span>
                  <span className={`font-mono ${en.amount < 0 ? 'text-green-600' : 'text-stone-900'}`}>
                    {en.amount < 0 ? '−' : ''}{fmt(Math.abs(en.amount))}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* Record a payment + mark paid */}
        <div className="flex items-center gap-2 mb-2">
          <Input
            type="number"
            placeholder="Payment amount"
            className="h-9 rounded-[4px] flex-1"
            value={payVal}
            onChange={(e) => setPayVal(e.target.value)}
            disabled={balance <= 0}
          />
          <Button
            size="sm"
            className="rounded-[4px] bg-[#021d49] hover:bg-[#032d6b] text-white"
            disabled={busy || balance <= 0}
            onClick={recordPayment}
          >
            <ArrowDownLeft className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-[4px] text-green-700 border-green-200 hover:bg-green-50"
            disabled={busy || balance <= 0}
            onClick={markPaid}
            title="Mark the whole balance paid"
          >
            <CheckCheck className="w-4 h-4 mr-1" /> Mark paid
          </Button>
        </div>

        {/* Opening balance / adjustment */}
        <div className="flex items-center gap-2">
          <select
            className="h-9 rounded-[4px] border border-stone-300 text-sm px-2 bg-white"
            value={adjType}
            onChange={(e) => setAdjType(e.target.value)}
          >
            <option value="opening">Opening</option>
            <option value="adjustment">Adjust (+/−)</option>
          </select>
          <Input
            type="number"
            placeholder="Amount"
            className="h-9 rounded-[4px] flex-1"
            value={adjVal}
            onChange={(e) => setAdjVal(e.target.value)}
          />
          <Button size="sm" variant="outline" className="rounded-[4px]" disabled={busy} onClick={addAdjust}>
            Add
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
